import { create } from 'zustand';
import type { Backend, Session, Workspace } from './backend';
import { SandboxBackend } from './sandbox';
import { NoMasjidError, SupabaseBackend } from './supabase';
import { setBackend } from './current';

const SUPA_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPA_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
const SANDBOX_FLAG = 'noorview.sandbox';
const DEMO_START = 'noorview.demoStart';

/**
 * The demo login. It only opens the in-browser sandbox: nothing is sent to Supabase, no real account is touched, and
 * real TVs can't be paired (their codes live in Supabase, the demo's codes only in this browser). The username and
 * password can be changed with VITE_DEMO_USERNAME and VITE_DEMO_PASSWORD in Netlify; they aren't secret, because all
 * they open is a copy of the system that lives in the visitor's own browser.
 */
export const DEMO = {
  username: ((import.meta.env.VITE_DEMO_USERNAME as string | undefined) || 'demo').trim().toLowerCase(),
  password: (import.meta.env.VITE_DEMO_PASSWORD as string | undefined) || 'NoorView-Demo',
};
export const isDemoLogin = (user: string, password: string) => user.trim().toLowerCase() === DEMO.username && password === DEMO.password;

/**
 * Picks the backend: Supabase when the build is configured with VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY,
 * otherwise the in-browser sandbox. The demo login (or ?sandbox in the address) switches this tab to the sandbox.
 */
function makeBackend(): Backend {
  let forceSandbox = false;
  try { forceSandbox = new URLSearchParams(location.search).has('sandbox') || sessionStorage.getItem(SANDBOX_FLAG) === '1'; if (forceSandbox) sessionStorage.setItem(SANDBOX_FLAG, '1'); } catch { /* ignore */ }
  if (SUPA_URL && SUPA_KEY && !forceSandbox) return new SupabaseBackend(SUPA_URL, SUPA_KEY);
  const sb = new SandboxBackend();
  // In a real deployment the sandbox is the demo: signing out of it goes back to the real sign-in page.
  if (SUPA_URL && SUPA_KEY) sb.onSignedOut = () => { try { sessionStorage.removeItem(SANDBOX_FLAG); } catch { /* ignore */ } location.assign('/login'); };
  return sb;
}
export const backend: Backend = makeBackend();
setBackend(backend);

/** True while this tab is the demo (a real deployment running the in-browser sandbox). */
export const demoMode = backend.kind === 'sandbox' && !!(SUPA_URL && SUPA_KEY);

/** Switches this tab to the demo and signs in to it (after a reload, so no Supabase client is left running). */
export function startDemo() {
  try { sessionStorage.setItem(SANDBOX_FLAG, '1'); sessionStorage.setItem(DEMO_START, '1'); } catch { /* ignore */ }
  location.assign('/login');
}
/** Whether the demo was just started and still needs signing in (read once). */
export function takeDemoStart(): boolean {
  try { const v = sessionStorage.getItem(DEMO_START) === '1'; sessionStorage.removeItem(DEMO_START); return v && backend.kind === 'sandbox'; } catch { return false; }
}

/** A virtual screen in a new window: a simulated TV that pairs with a code, for the sandbox and the demo. */
export function openVirtualScreen() { window.open('/display?sandbox', '_blank', 'popup,width=1280,height=760'); }

export { ONLINE_WINDOW_MS, screenStatus, STATUS_LABEL, STATUS_PILL, seenAgo, type ScreenStatus } from './status';

interface AppState {
  session: Session | null;
  ws: Workspace | null;
  error: string | null;
  /** Signed in, but the account has no masjid yet (first sign-in). */
  needsMasjid: boolean;
  /** Clock for status calculations, refreshed every few seconds. */
  now: number;
  reload: () => Promise<void>;
  setSession: (s: Session | null) => void;
}

export const useApp = create<AppState>((set) => ({
  session: backend.session(),
  ws: null,
  error: null,
  now: Date.now(),
  needsMasjid: false,
  reload: async () => {
    try { set({ ws: await backend.load(), error: null, needsMasjid: false, now: Date.now() }); }
    catch (e) {
      if (e instanceof NoMasjidError) { set({ needsMasjid: true, error: null }); return; }
      set({ error: e instanceof Error ? e.message : 'Couldn’t load your masjid.' });
    }
  },
  setSession: (s) => set({ session: s }),
}));

let started = false;
/** Keeps the workspace fresh: reloads on any change (other tabs, devices, other editors) and ticks the status clock. */
export function startSync() {
  if (started) return;
  started = true;
  let pending: ReturnType<typeof setTimeout> | null = null;
  const reload = () => { if (pending) return; pending = setTimeout(() => { pending = null; void useApp.getState().reload(); }, 60); };
  backend.subscribe(() => reload());
  setInterval(() => useApp.setState({ now: Date.now() }), 5000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) reload(); });
  void useApp.getState().reload();
}

/* ---------- appearance ---------- */
export type Theme = 'legacy' | 'light' | 'dark';
export const useTheme = create<{ theme: Theme; set: (t: Theme) => void }>((set) => ({
  theme: (document.documentElement.dataset.theme as Theme) || 'light',
  set: (t) => {
    document.documentElement.dataset.theme = t;
    try { localStorage.setItem('noorview.theme', t); } catch { /* ignore */ }
    set({ theme: t });
  },
}));

/** Interface style: NoorView's liquid glass, or Simple (flat, with no shadows or blur). Works with every theme. */
export type UiStyle = 'glass' | 'simple';
export const useUiStyle = create<{ ui: UiStyle; set: (u: UiStyle) => void }>((set) => ({
  ui: document.documentElement.dataset.ui === 'simple' ? 'simple' : 'glass',
  set: (u) => {
    if (u === 'simple') document.documentElement.dataset.ui = 'simple'; else delete document.documentElement.dataset.ui;
    try { localStorage.setItem('noorview.ui', u); } catch { /* ignore */ }
    set({ ui: u });
  },
}));

/* ---------- toasts ---------- */
export interface Toast { id: number; text: string; bad?: boolean; action?: { label: string; run: () => void }; leaving?: boolean }
let tid = 0;
export const useToasts = create<{ list: Toast[] }>(() => ({ list: [] }));
export function toast(text: string, opts: { bad?: boolean; action?: Toast['action']; ms?: number } = {}) {
  const t: Toast = { id: ++tid, text, bad: opts.bad, action: opts.action };
  useToasts.setState((s) => ({ list: [...s.list.slice(-2), t] }));
  // Fade out first, then go.
  setTimeout(() => {
    useToasts.setState((s) => ({ list: s.list.map((x) => (x.id === t.id ? { ...x, leaving: true } : x)) }));
    setTimeout(() => useToasts.setState((s) => ({ list: s.list.filter((x) => x.id !== t.id) })), 220);
  }, opts.ms ?? (opts.bad ? 6000 : 3500));
}
/**
 * Runs a backend call and shows its error as a toast. Resolves to the call's result (or `true` for calls that return
 * nothing) on success, and to undefined if it failed.
 */
export async function attempt<T>(fn: () => Promise<T>, okText?: string): Promise<(T extends void ? true : T) | undefined> {
  try {
    const r = await fn();
    if (okText) toast(okText);
    return (r === undefined ? true : r) as T extends void ? true : T;
  } catch (e) {
    toast(e instanceof Error ? e.message : 'Something went wrong. Please try again.', { bad: true });
    return undefined;
  }
}

/** Live and upcoming messages and appeals for one screen (stable while nothing changes, for previews). */
const bcCache = new Map<string, { src: unknown; out: import('@noorview/core').Broadcast[] }>();
export function useScreenBroadcasts(screenId: string | undefined) {
  const list = useApp((s) => s.ws?.broadcasts);
  if (!list || !screenId) return undefined;
  const c = bcCache.get(screenId);
  if (c && c.src === list) return c.out;
  const out = list.filter((b) => (!b.endsAt || Date.parse(b.endsAt) > Date.now()) && (!b.screenIds || b.screenIds.includes(screenId)));
  bcCache.set(screenId, { src: list, out });
  return out;
}
